import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useMemo, useState } from 'react'
import { LoanType, loanTypeMap } from 'reducers/loanDetail.reducer'
import { getAdminConfig, setAdminConfig } from 'services/apis/admin'
import { svgLoading } from 'stories/assets'
import { Button, ButtonGroup, PlainTable } from 'stories/components'
import { confirm, value2YN } from 'utils'

import { AddExpirationDateTypeModal } from './AddExpirationDateTypeModal'
import { IExpirationDateConfig, IExpirationDateConfigItem } from './type'

export const ExpirationDateType = () => {
  const [action, setAction] = useState('')
  const [config, setConfig] = useState<IExpirationDateConfig>()
  const [editingKey, setEditingKey] = useState<string>()
  const [loanType, setLoanType] = useState(LoanType.DEFAULT)

  useEffect(() => {
    setAction('setting')
    getAdminConfig('expirationDateType')
      .then((config) => setConfig(config))
      .finally(() => setAction(''))
  }, [])

  const onEdit = (key: string) => {
    setEditingKey(key)
  }

  const orderOptions = useMemo(() => {
    if (!config) return []

    const rlt: Number[] = []
    Object.keys(config[loanType].data).map((_, index) => rlt.push(index + 1))
    return rlt
  }, [config, loanType])

  const tableData = useMemo(() => {
    if (!config) return []

    return config[loanType].order.map((key, index) => {
      const { title, days, type, seasonType, isCoBorrower, isCustom, calculateBasedOn } = config[loanType].data[key]
      return [
        `${index + 1}`,
        title,
        type ? '' : days,
        value2YN(type),
        value2YN(seasonType),
        calculateBasedOn == 2 ? 'Closing Date' : 'Today Date',
        value2YN(isCoBorrower),
        <select
          value={index + 1}
          className="py-1 border-gray-300 text-[15px]"
          onChange={(e) => onChangeOrder(index, Number(e.target.value) - 1)}
        >
          {orderOptions.map((item: any, index1) => {
            return (
              <option key={index1} value={`${item}`} disabled={item === index + 1}>
                {item}
              </option>
            )
          })}
        </select>,
        <div className="flex items-center gap-2">
          <span className="btn-icon" onClick={() => onEdit(key)}>
            <PencilSquareIcon className="w-4 h-4" />
          </span>
          {!!isCustom && (
            <span className="btn-icon" onClick={() => onDelete(key)}>
              <TrashIcon className="w-4 h-4 text-red-500" />
            </span>
          )}
        </div>,
      ]
    })
  }, [config, loanType])

  if (!config) return <LayoutLoading show />

  const onDelete = async (key: string) => {
    const result = await confirm('Are you sure you want to delete?')
    if (!result) return

    const newConfig = cloneDeep(config)
    delete newConfig[loanType].data[key]

    const index = newConfig[loanType].order.indexOf(key)
    newConfig[loanType].order.splice(index, 1)
    setConfig(newConfig)

    setAction('setting')
    await setAdminConfig('expirationDateType', config)
    setAction('')
  }

  const onChangeOrder = async (prev: number, newIdx: number) => {
    const newConfig = cloneDeep(config)
    const item = newConfig[loanType].order[prev]
    newConfig[loanType].order.splice(prev, 1)
    newConfig[loanType].order.splice(newIdx, 0, item)
    setConfig(newConfig)

    setAction('setting')
    await setAdminConfig('expirationDateType', newConfig)
    setAction('')
  }

  const onAdd = () => {
    setEditingKey('')
  }

  const onCloseModal = async (item: IExpirationDateConfigItem | null) => {
    if (!item) {
      setEditingKey(undefined)
      return
    }

    const newConfig = cloneDeep(config)

    if (editingKey) {
      const editingItem = newConfig[loanType].data[editingKey]
      if (editingItem.isCoBorrower) item.isCoBorrower = editingItem.isCoBorrower
      if (editingItem.isCustom) item.isCustom = editingItem.isCustom
      newConfig[loanType].data[editingKey] = item
    } else {
      const newKey = `${Date.now()}`
      newConfig[loanType].data[newKey] = {
        ...item,
        isCoBorrower: 0,
        isCustom: 1,
      }
      newConfig[loanType].order.push(newKey)
    }
    setConfig(newConfig)

    setAction('setting')
    await setAdminConfig('expirationDateType', newConfig)
    setAction('')
    setEditingKey(undefined)
  }

  return (
    <div className="relative">
      <LayoutLoading show={action !== ''} />
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold flex items-center pb-5">
          Expiration Date
          {action && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h1>
      </div>
      <div className="relative mb-6">
        <div className="flex items-center justify-between">
          <ButtonGroup title={loanTypeMap} onChange={setLoanType} value={`${loanType}`} />
          <Button onClick={onAdd}>Add</Button>
        </div>

        <div className="overflow-auto sm:rounded-lg">
          <PlainTable
            thClass="!px-4 !py-2"
            tdClass="px-4 py-2 whitespace-nowrap"
            header={[
              'No',
              'Type',
              'Days',
              'Only Expired Date?',
              'Seasoning?',
              'Based on',
              'CoBorrower',
              'Order',
              'Action',
            ]}
            data={tableData}
          />
        </div>

        {editingKey !== undefined && (
          <AddExpirationDateTypeModal data={config[loanType].data[editingKey]} onClose={onCloseModal} />
        )}
      </div>
    </div>
  )
}
